import React, { createContext, useContext, useCallback, useEffect, useState } from 'react';

/**
 * Quick Access Item - Represents a recently used plugin
 */
export interface QuickAccessItem {
  pluginId: string;
  title: string;
  path: string;
  icon?: string;
  group?: string;
  accessedAt: number;
  accessCount: number;
}

interface QuickAccessContextValue {
  items: QuickAccessItem[];
  trackAccess: (pluginId: string, metadata: { title: string; path: string; icon?: string; group?: string }) => void;
  removeItem: (pluginId: string) => void;
  clearAll: () => void;
}

const STORAGE_KEY = 'empowernow_quick_access';
const STORAGE_VERSION_KEY = 'empowernow_quick_access_version';
const CURRENT_VERSION = 2;
const MAX_ITEMS = 10;

const defaultContext: QuickAccessContextValue = {
  items: [],
  trackAccess: () => {},
  removeItem: () => {},
  clearAll: () => {},
};

const QuickAccessContext = createContext<QuickAccessContextValue>(defaultContext);

function loadFromStorage(): QuickAccessItem[] {
  try {
    const storedVersion = localStorage.getItem(STORAGE_VERSION_KEY);
    const version = storedVersion ? parseInt(storedVersion, 10) : 0;

    if (version < CURRENT_VERSION) {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.setItem(STORAGE_VERSION_KEY, String(CURRENT_VERSION));
      return [];
    }

    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((item: unknown): item is Record<string, unknown> =>
        item !== null &&
        typeof item === 'object' &&
        typeof (item as Record<string, unknown>).pluginId === 'string' &&
        typeof (item as Record<string, unknown>).title === 'string' &&
        typeof (item as Record<string, unknown>).path === 'string'
      )
      .map((item) => ({
        pluginId: item.pluginId as string,
        title: item.title as string,
        path: item.path as string,
        icon: (item.icon as string) || undefined,
        group: (item.group as string) || undefined,
        accessedAt: (item.accessedAt as number) || Date.now(),
        accessCount: (item.accessCount as number) || 1,
      }));
  } catch (e) {
    console.warn('[QuickAccess] Failed to load from localStorage:', e);
    return [];
  }
}

function saveToStorage(items: QuickAccessItem[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    localStorage.setItem(STORAGE_VERSION_KEY, String(CURRENT_VERSION));
  } catch (e) {
    console.warn('[QuickAccess] Failed to save to localStorage:', e);
  }
}

export function QuickAccessProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<QuickAccessItem[]>([]);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    const loaded = loadFromStorage();
    setItems(loaded);
    setInitialized(true);
  }, []);

  useEffect(() => {
    if (initialized && items.length >= 0) {
      saveToStorage(items);
    }
  }, [items, initialized]);

  const trackAccess = useCallback((
    pluginId: string,
    metadata: { title: string; path: string; icon?: string; group?: string }
  ) => {
    setItems(prev => {
      const existingIndex = prev.findIndex(item => item.pluginId === pluginId);
      const now = Date.now();

      if (existingIndex >= 0) {
        const existing = prev[existingIndex];
        const updated = {
          ...existing,
          ...metadata,
          accessedAt: now,
          accessCount: existing.accessCount + 1,
        };
        return [updated, ...prev.filter((_, i) => i !== existingIndex)].slice(0, MAX_ITEMS);
      }

      const newItem: QuickAccessItem = {
        pluginId,
        ...metadata,
        accessedAt: now,
        accessCount: 1,
      };
      return [newItem, ...prev].slice(0, MAX_ITEMS);
    });
  }, []);

  const removeItem = useCallback((pluginId: string) => {
    setItems(prev => prev.filter(item => item.pluginId !== pluginId));
  }, []);

  const clearAll = useCallback(() => {
    setItems([]);
  }, []);

  const value: QuickAccessContextValue = {
    items,
    trackAccess,
    removeItem,
    clearAll,
  };

  return (
    <QuickAccessContext.Provider value={value}>
      {children}
    </QuickAccessContext.Provider>
  );
}

export function useQuickAccess(): QuickAccessContextValue {
  return useContext(QuickAccessContext);
}

export default QuickAccessContext;
